import React, { useState, useEffect, useCallback } from 'react';
import BusinessAssetsModal from './BusinessAssetsModal';

// Map component keys to backend URL segments
const URL_MAP = {
  accountsReceivable: 'accounts-receivable',
  officeEquipment: 'office-equipments',
  machineryEquipment: 'machinery-equipments',
  inventory: 'inventories',
  partnershipInterest: 'partnership-interests',
  customerList: 'customer-lists',
  otherBusinessProperty: 'other-business-properties',
};

const assetTypes = [
  { key: 'accountsReceivable', label: 'Accounts Receivable' },
  { key: 'officeEquipment', label: 'Office Equipment' },
  { key: 'machineryEquipment', label: 'Machinery, Fixtures & Tools' },
  { key: 'inventory', label: 'Inventory' },
  { key: 'partnershipInterest', label: 'Partnership Interests' },
  { key: 'customerList', label: 'Customer Lists' },
  { key: 'otherBusinessProperty', label: 'Other Business Property' },
];
const jsonKeyMap = {
  accountsReceivable: 'accounts_receivable',
  officeEquipment:   'office_equipments',
  machineryEquipment:'machinery_equipments',
  inventory:         'inventories',
  partnershipInterest:'partnership_interests',
  customerList:      'customer_lists',
  otherBusinessProperty: 'other_business_properties',
};
export default function BusinessAssetsSection({ caseId, onSave,exemptions, scope }) {
  const [assets, setAssets] = useState({});
  const [selectedType, setSelectedType] = useState('');
  const [modalType, setModalType] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

useEffect(() => {
  (async () => {
    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/case/${caseId}/business-assets/`,
      {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
        },
      }
    );
    if (!res.ok) return;
    const json = await res.json();
    const biz = json.business || {};
    const mapped = {};
    assetTypes.forEach(({ key }) => {
      mapped[key] = biz[jsonKeyMap[key]] || [];
    });
    setAssets(mapped);
  })();
}, [caseId]);

  const openAdd = typeKey => {
    if (!typeKey) return;
    setModalType(typeKey);
    setEditingItem(null);
    setIsModalOpen(true);
  };

  const handleSave = item => {
    setAssets(prev => {
      const list = prev[modalType] || [];
      const exists = list.find(i => i.id === item.id);
      const updated = exists
        ? list.map(i => i.id === item.id ? item : i)
        : [...list, item];
      const newAssets = { ...prev, [modalType]: updated };
      onSave(newAssets);
      return newAssets;
    });
    setIsModalOpen(false);
    setEditingItem(null);
  };

  const handleDelete = useCallback(async (typeKey, id) => {
    const urlType = URL_MAP[typeKey] || typeKey;
    const res = await fetch(`${process.env.REACT_APP_API_URL}/case/${caseId}/${urlType}/${id}/delete/`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` },
    });
    if (res.ok) {
      setAssets(prev => ({
        ...prev,
        [typeKey]: prev[typeKey].filter(i => i.id !== id),
      }));
    } else {
      alert('Failed to delete');
    }
  }, [caseId]);

  return (
    <section className="mt-12 space-y-8">
      <header className="flex items-center justify-between bg-gray-100 p-4 rounded-md">
        <h2 className="text-2xl font-semibold">Business Assets</h2>
        <div className="flex space-x-2">
          <select
            value={selectedType}
            onChange={e => setSelectedType(e.target.value)}
            className="border border-gray-300 rounded-md p-2"
          >
            <option value="">Select Asset…</option>
            {assetTypes.map(at => (
              <option key={at.key} value={at.key}>{at.label}</option>
            ))}
          </select>
          <button
            onClick={() => openAdd(selectedType)}
            disabled={!selectedType}
            className="px-4 py-2 bg-blue-600 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Add Asset
          </button>
        </div>
      </header>
      {assetTypes
        .filter(({ key }) => (assets[key] || []).length > 0)
        .map(({ key, label }) => (
          <section key={key} className="mt-6">
            <h3 className="text-xl font-semibold mb-3">{label}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {(assets[key] || []).map(item => (
                <div
                  key={item.id}
                  className="bg-white rounded-lg shadow p-6 flex flex-col hover:shadow-lg transition-shadow"
                >
                  <ul className="space-y-1 text-sm text-gray-700 mb-4 flex-grow">
                    {Object.entries(item)
                      .filter(([field]) => !['id', 'case', 'form_number'].includes(field))
                      .map(([field, value]) => (
                        <li key={field}><strong>
                          {field.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}:
                        </strong> {value}</li>
                      ))}
                  </ul>
                  <div className="mt-auto flex space-x-2">
                    <button
                      onClick={() => { setModalType(key); setEditingItem(item); setIsModalOpen(true); }}
                      className="px-3 py-1 bg-yellow-500 text-white rounded"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(key, item.id)}
                      className="px-3 py-1 bg-red-600 text-white rounded"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        ))}
      <BusinessAssetsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        type={modalType}
        item={editingItem}
        onSave={handleSave}
        caseId={caseId}
        exemptions={exemptions}
        scope={scope}
      />
    </section>
  );
}